import { handle400Statuses, sendApiRequest } from '@services/api';
import { logNetRequest, logError } from '@utils/usefulFuncs';
import { BASEURL } from '@services/network';
import { createAuthedEventSource } from '@utils/streamAuth';

const BASE_URL = 'v2/genai/score_backfills';

export const SCOPE_OPTIONS = {
  FULL_HISTORY: 'full_history',
  LATEST_PER_COMPANY: 'latest_per_company',
};

export const UNIVERSE_KINDS = {
  WHOLE_CORPUS: 'whole_corpus',
  INDEX: 'index',
  TICKERS: 'tickers',
};

export const BACKFILL_STATUSES = {
  QUEUED: 'queued',
  RUNNING: 'running',
  COMPLETED: 'completed',
  FAILED: 'failed',
  CANCELED: 'canceled',
};

// canceled is terminal alongside completed/failed — a canceled run's partial scores are
// invalidated, so it has no snapshot and never appears in a feed/export read.
export const BACKFILL_TERMINAL_STATUSES = [
  BACKFILL_STATUSES.COMPLETED,
  BACKFILL_STATUSES.FAILED,
  BACKFILL_STATUSES.CANCELED,
];

// The two `status` values a successful re_export can return — 200 does not imply a write.
export const RE_EXPORT_STATUSES = {
  RE_EXPORTED: 're_exported',
  NOTHING_TO_EXPORT: 'nothing_to_export',
};

// GET /specs — discovery: enabled specs + universe constraints (drives the picker).
export const fetchScoreSpecs = async () => {
  const url = `${BASE_URL}/specs`;
  const { status, data, error } = await sendApiRequest('get', url);
  logNetRequest(url, status, data, error);
  handle400Statuses(status);

  if (status === 200 && data) {
    return { status, specs: data.specs || [], universeOptions: data.universe_options || null, error: null };
  }
  return { status, specs: [], universeOptions: null, error: error || data?.error || 'Unable to load score specs.' };
};

// POST /preview — dry-run: candidate count + cost estimate. Never writes rows, never spends.
export const previewScoreBackfill = async (body) => {
  const url = `${BASE_URL}/preview`;
  const { status, data, error } = await sendApiRequest('post', url, body);
  logNetRequest(url, status, data, error);
  handle400Statuses(status);

  if (status === 200 && data) {
    return { status, preview: data, error: null };
  }
  return { status, preview: null, error: error || data?.error || 'Unable to preview backfill.' };
};

const createErrorFallback = (status) => {
  if (status === 429) return 'At capacity — 2 backfills are already running. Try again later.';
  if (status === 409) return 'The backfill was rejected before it could start.';
  if (status === 502) return 'The scoring engine is unreachable. Try again shortly.';
  return 'Unable to start the backfill.';
};

// POST / — create + start. 400/409/429/502 all mean "not started"; error carries the reason.
export const createScoreBackfill = async (body) => {
  const { status, data, error } = await sendApiRequest('post', BASE_URL, body);
  logNetRequest(BASE_URL, status, data, error);
  handle400Statuses(status);

  if (status === 200 && data) {
    return { status, job: data, error: null };
  }
  return { status, job: null, error: data?.error || error || createErrorFallback(status) };
};

// GET / — list the current user's jobs (most-recent first).
export const fetchScoreBackfills = async () => {
  const { status, data, error } = await sendApiRequest('get', BASE_URL);
  logNetRequest(BASE_URL, status, data, error);
  handle400Statuses(status);

  if (status === 200 && data && Array.isArray(data.score_backfills)) {
    return { status, jobs: data.score_backfills, error: null };
  }
  return { status, jobs: [], error: error || data?.error || null };
};

// GET /:id — one job (poll fallback).
export const fetchScoreBackfill = async (id) => {
  const url = `${BASE_URL}/${id}`;
  const { status, data, error } = await sendApiRequest('get', url);
  logNetRequest(url, status, data, error);
  handle400Statuses(status);

  if (status === 200 && data && data.score_backfill) {
    return { status, job: data.score_backfill, error: null };
  }
  return { status, job: null, error: error || data?.error || 'Not found' };
};

const cancelErrorFallback = (status) => {
  if (status === 422) return 'That backfill no longer exists — there is nothing to cancel.';
  if (status === 502) return 'The cancel request wasn’t recorded. Try again.';
  return 'Unable to cancel the backfill.';
};

// POST /:id/cancel — cooperative and asynchronous. A 200 only means the request was recorded:
// the worker stops launching batches at its next poll (~5s, plus any in-flight batch's drain),
// invalidates the run's partial scores, and finalizes the job as `canceled` with no snapshot.
// Watch the SSE stream / poll for that terminal status — do not treat the 200 as "canceled".
// Idempotent: re-requesting just re-stamps the flag, and flagging a finished run still returns 200.
export const cancelScoreBackfill = async (id) => {
  const url = `${BASE_URL}/${id}/cancel`;
  const { status, data, error } = await sendApiRequest('post', url);
  logNetRequest(url, status, data, error);
  handle400Statuses(status);

  // A 200 is the request being recorded, which an unreadable body doesn't undo — don't hand the
  // caller a 200 alongside an error, since every other function here only errors on a non-200.
  if (status === 200) {
    return { status, result: data || null, error: null };
  }
  return { status, result: null, error: data?.message || data?.error || error || cancelErrorFallback(status) };
};

const reExportErrorFallback = (status) => {
  if (status === 422) return 'This run can’t be re-published — it has no scores to snapshot.';
  if (status === 502) return 'The snapshot didn’t publish. Try again.';
  if (status === 503) return 'The scoring engine is busy. Try again shortly.';
  return 'Unable to re-publish the snapshot.';
};

// POST /:id/re_export — re-emits a completed run's S3 snapshot from its current factor_scores.
// No re-scoring, no model spend; synchronous (~seconds). Never overwrites: every call writes a
// new uniquely-stamped `…__reexport_<ts>/` folder and the run's s3_prefix moves to the latest.
// 200 carries either RE_EXPORTED (with s3_bucket/s3_prefix) or NOTHING_TO_EXPORT (nothing written).
// `result` is returned on failures too so a 502 caller can read `repairable` (true = the folder
// uploaded but the announce didn't fire; retrying the same id is safe).
export const reExportScoreBackfill = async (id) => {
  const url = `${BASE_URL}/${id}/re_export`;
  const { status, data, error } = await sendApiRequest('post', url);
  logNetRequest(url, status, data, error);
  handle400Statuses(status);

  // Only the two documented outcomes count as success. A 200 whose body didn't parse, or that
  // carries a status this client doesn't know, is an error — reporting it as `nothing_to_export`
  // would tell the analyst the opposite of what the server just did.
  const succeeded =
    data?.status === RE_EXPORT_STATUSES.RE_EXPORTED || data?.status === RE_EXPORT_STATUSES.NOTHING_TO_EXPORT;
  if (status === 200 && succeeded) {
    return { status, result: data, error: null };
  }
  return {
    status,
    result: data || null,
    error:
      data?.message ||
      data?.error ||
      error ||
      (status === 200 ? 'The re-publish response couldn’t be read. Check the run before retrying.' : null) ||
      reExportErrorFallback(status),
  };
};

// GET /:job_id/stream — SSE progress. Each progress/complete frame carries the full job object
// as data.response. Returns { source, close }. onError receives the server error payload
// ({ error }) when present, or the raw transport event otherwise.
export const openScoreBackfillStream = (jobId, { onOpen, onProgress, onComplete, onError } = {}) => {
  if (!jobId) {
    onError?.({ error: 'Missing job_id' });
    return { close: () => {} };
  }

  const url = `${BASEURL}/api/${BASE_URL}/${jobId}/stream`;
  const source = createAuthedEventSource(url, { heartbeatTimeout: 60000 });

  const parseJob = (event) => {
    try {
      return JSON.parse(event.data)?.response || null;
    } catch (e) {
      return null;
    }
  };

  source.addEventListener('open', () => onOpen?.());
  source.addEventListener('progress', (event) => onProgress?.(parseJob(event)));
  source.addEventListener('complete', (event) => onComplete?.(parseJob(event)));
  source.addEventListener('error', (event) => {
    let payload = null;
    try {
      payload = event?.data ? JSON.parse(event.data) : null;
    } catch (e) {
      payload = null;
    }
    onError?.(payload || event);
  });

  return {
    source,
    close: () => {
      try {
        source.close();
      } catch (e) {
        logError('scoreBackfills sse close', e);
      }
    },
  };
};
